import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, ErrorState, Select, Table } from '@react-apps/design-system';
import type { ColumnDef } from '@react-apps/design-system';
import {
  CONTENT_STATUSES,
  CONTENT_TYPES,
  type ContentItemListEntry,
  type ContentStatus,
  type ContentType,
} from '../../domain/entities/content.entity';
import { useContentItems } from '../hooks/use-content-items';
import {
  CONTENT_STATUS_LABELS,
  CONTENT_TYPE_LABELS,
  ContentStatusBadge,
  contentDisplayName,
  getApiErrorMessage,
} from '../display';
import { Pagination } from './Pagination';
import {
  EMPTY_QUESTION_FILTERS,
  QuestionFilters,
  countQuestionFilters,
  type QuestionFilterValues,
} from './QuestionFilters';
import { CreateContentItemModal } from './CreateContentItemModal';
import { RefreshButton } from '@/shared/RefreshButton';
import {
  readPageParam,
  writePageParam,
} from '@/shared/hooks/use-page-query-param';
import {
  readFiltersParams,
  writeFiltersParams,
} from '@/shared/hooks/use-url-filters';

const PER_PAGE = 10;

interface ContentItemsFilters extends QuestionFilterValues {
  type: ContentType | '';
  status: ContentStatus | '';
}

const EMPTY_CONTENT_ITEMS_FILTERS: ContentItemsFilters = {
  type: '',
  status: '',
  ...EMPTY_QUESTION_FILTERS,
};

/**
 * Sem coluna Tipo: "Conteúdo" já diz `Questão …` / `Prova …`, e o badge
 * repetia a informação.
 */
const columns: ColumnDef<ContentItemListEntry>[] = [
  {
    key: 'content',
    header: 'Conteúdo',
    render: (row) =>
      contentDisplayName({
        type: row.type,
        id: row.id,
        label: row.label,
        examKind:
          (row.body as { examKind?: 'REAL_EXAM' | 'MOCK_EXAM' } | null)
            ?.examKind ?? null,
        idForm: 'full',
      }),
  },
  {
    key: 'status',
    header: 'Status',
    render: (row) => <ContentStatusBadge status={row.status} />,
  },
  {
    key: 'version',
    header: 'Versão',
    render: (row) => (
      <span className="text-(--color-text-secondary)">v{row.version}</span>
    ),
  },
];

export function ContentItemsPanel() {
  const navigate = useNavigate();
  // Página e filtros vivem na URL (não em useState) para sobreviver a
  // "Voltar" do detalhe do conteúdo — ver
  // primum-core-root/docs/plans/done/admin-dashboard-pagination-url-sync.md. Trocar um filtro também
  // reseta a página; as duas mudanças vão num único setSearchParams (ver
  // aviso em use-page-query-param.ts).
  const [searchParams, setSearchParams] = useSearchParams();
  const page = readPageParam(searchParams);
  const filters = readFiltersParams(searchParams, EMPTY_CONTENT_ITEMS_FILTERS);
  const { type, status, format, annulled, examId } = filters;
  const setPage = (next: number) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writePageParam(params, next);
        return params;
      },
      { replace: true },
    );
  };
  const setFilterAndResetPage = (next: Partial<ContentItemsFilters>) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writeFiltersParams(
          params,
          { ...filters, ...next },
          EMPTY_CONTENT_ITEMS_FILTERS,
        );
        writePageParam(params, 1);
        return params;
      },
      { replace: true },
    );
  };
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const { data, isLoading, isError, error, refetch, isFetching } =
    useContentItems({
      page,
      perPage: PER_PAGE,
      type: type || undefined,
      status: status || undefined,
      questionFormat: format || undefined,
      annulled: annulled ? annulled === 'true' : undefined,
      examId: examId || undefined,
    });
  const questionFilterCount = countQuestionFilters(filters);
  const hasFilters = Boolean(type || status || questionFilterCount);
  const clearFilters = () => setFilterAndResetPage(EMPTY_CONTENT_ITEMS_FILTERS);
  const clearFiltersButton = (
    <Button type="button" variant="ghost" size="sm" onClick={clearFilters}>
      Limpar filtros
    </Button>
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-44">
            <Select
              label="Tipo"
              value={type}
              // Os filtros de questão só existem com Tipo = Questão: trocar o
              // tipo os limpa no mesmo `setSearchParams`, senão a URL ficaria
              // com filtro invisível e o back responderia 400.
              onChange={(e) => {
                const next = e.target.value as ContentType | '';
                setFilterAndResetPage(
                  next === 'QUESTION'
                    ? { type: next }
                    : { type: next, ...EMPTY_QUESTION_FILTERS },
                );
              }}
              options={[
                { value: '', label: 'Todos' },
                ...CONTENT_TYPES.map((t) => ({
                  value: t,
                  label: CONTENT_TYPE_LABELS[t],
                })),
              ]}
            />
          </div>
          <div className="w-44">
            <Select
              label="Status"
              value={status}
              onChange={(e) =>
                setFilterAndResetPage({
                  status: e.target.value as ContentStatus | '',
                })
              }
              options={[
                { value: '', label: 'Todos' },
                ...CONTENT_STATUSES.map((s) => ({
                  value: s,
                  label: CONTENT_STATUS_LABELS[s],
                })),
              ]}
            />
          </div>
          {type === 'QUESTION' && (
            <QuestionFilters
              value={{ format, annulled, examId }}
              onApply={(next) => setFilterAndResetPage(next)}
            />
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <RefreshButton
            onRefresh={() => void refetch()}
            isRefreshing={isFetching}
          />
          <Button onClick={() => setIsCreateOpen(true)}>Criar conteúdo</Button>
        </div>
      </div>

      {isError ? (
        <div className="space-y-2 text-center">
          <ErrorState
            message={getApiErrorMessage(
              error,
              'Não foi possível carregar os conteúdos.',
            )}
            onRetry={() => void refetch()}
          />
          {hasFilters && clearFiltersButton}
        </div>
      ) : (
        <>
          <Table
            columns={columns}
            data={data?.items ?? []}
            keyExtractor={(row) => row.id}
            loading={isLoading}
            emptyMessage={
              questionFilterCount > 0
                ? 'Nenhuma questão com esses filtros.'
                : hasFilters
                  ? 'Nenhum conteúdo com esses filtros.'
                  : 'Nenhum conteúdo encontrado.'
            }
            striped
            onRowClick={(row) => void navigate(`/content/items/${row.id}`)}
          />
          {hasFilters && !isLoading && data?.items.length === 0 && (
            <div className="text-center">{clearFiltersButton}</div>
          )}
          <Pagination
            page={page}
            total={data?.total ?? 0}
            perPage={PER_PAGE}
            loading={isLoading}
            label="conteúdo"
            onPageChange={setPage}
          />
        </>
      )}

      <CreateContentItemModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />
    </div>
  );
}
